import { SwapOutlined } from '@ant-design/icons';
import { useCustomMutation, useTranslate } from '@refinedev/core';
import { Alert, Button, Modal, Select, Space, Tooltip, Typography } from 'antd';
import { useCallback, useMemo, useState } from 'react';
import { practiceMemberLabel, usePracticeMembers } from '../../hooks';

const { Text } = Typography;

export type TransferClientActionProps = {
  /** `users/<key>` — the CLIENT who would move. */
  subject: string;
  /** How the client is named in the confirmation copy. Never a row key if it can be helped. */
  clientLabel: string;
  /** Practitioners this client must NOT be offered a transfer to — normally their current one. */
  excludeAssignees?: string[];
  disabled?: boolean;
};

/**
 * E07 · RequestClientTransfer — "ask this client to move to a colleague" (M7, board cc-rtm; SDL
 * `be/sharing.gql` §1).
 *
 * ─── IT ASKS. IT DOES NOT MOVE ANYONE ──────────────────────────────────────────────────────────
 * This is the whole reason the capability is two mutations and not one. The button records a
 * REQUEST; the client answers it on their phone; only then does `reassignClient` (on the Unassigned
 * Clients page) succeed. Founder, 2026-07-20: client approval is a hard precondition, so a
 * practitioner cannot hand somebody's care to a colleague by administrative act.
 *
 * The copy therefore never says "transferred" — it says the client has been asked. A therapist who
 * closed this modal believing the move had happened, and discovered a week later that it had not,
 * would have lost a week of somebody's care to a wording choice.
 *
 * ─── A DECLINE IS INVISIBLE, AND THAT IS THE DESIGN ────────────────────────────────────────────
 * `approved: false` records nothing at all, so the portal cannot show "declined" — only "still
 * waiting". Recording a client's refusal would put a judgement about them into a log their practice
 * can read, for no purpose the product has. The modal says the request stays open so the absence is
 * not mistaken for a failure of the button.
 */
export const TransferClientAction = ({
  subject,
  clientLabel,
  excludeAssignees,
  disabled,
}: TransferClientActionProps) => {
  const translate = useTranslate();
  const [open, setOpen] = useState(false);
  const [newAssignee, setNewAssignee] = useState<string | undefined>();
  const [failure, setFailure] = useState<string | null>(null);
  const [requested, setRequested] = useState(false);
  const [saving, setSaving] = useState(false);

  // The roster is only fetched once the modal is opened — a clients table can render fifty of these
  // and fifty identical practice-roster queries on page load would be a self-inflicted outage.
  const { members } = usePracticeMembers({ enabled: open });
  const { mutateAsync: runRequest } = useCustomMutation();

  const options = useMemo(
    () =>
      members
        .filter((member) => !(excludeAssignees ?? []).includes(member._id))
        .map((member) => ({ value: member._id, label: practiceMemberLabel(member) })),
    [excludeAssignees, members],
  );

  const close = useCallback(() => {
    setOpen(false);
    setNewAssignee(undefined);
    setFailure(null);
    setRequested(false);
  }, []);

  const submit = useCallback(async () => {
    if (!newAssignee) {
      return;
    }

    setSaving(true);
    setFailure(null);

    try {
      await runRequest({
        url: '',
        method: 'post',
        values: {},
        errorNotification: false,
        successNotification: false,
        meta: {
          operation: 'requestClientTransfer',
          fields: ['success', 'subject'],
          variables: {
            input: {
              name: 'input',
              type: 'RequestClientTransferInput!',
              value: { subject, newAssignee },
            },
          },
        },
      });

      setRequested(true);
    } catch (error) {
      // The server's own coded message names which precondition failed (not a practice admin, the
      // colleague is in another practice, …) and is already actionable in both languages.
      setFailure(
        (error as { message?: string })?.message
        || translate('portalClients.transfer.failed'),
      );
    } finally {
      setSaving(false);
    }
  }, [newAssignee, runRequest, subject, translate]);

  const assigneeLabel =
    options.find((option) => option.value === newAssignee)?.label ?? '';

  return (
    <>
      <Tooltip title={translate('portalClients.transfer.tip')}>
        <Button
          icon={<SwapOutlined />}
          disabled={disabled}
          onClick={() => setOpen(true)}
          data-testid="cc-clients-list-transfer-button"
        />
      </Tooltip>

      <Modal
        open={open}
        title={translate('portalClients.transfer.title')}
        okText={
          requested
            ? translate('portalClients.transfer.close')
            : translate('portalClients.transfer.send')
        }
        cancelButtonProps={requested ? { style: { display: 'none' } } : undefined}
        cancelText={translate('buttons.cancel')}
        confirmLoading={saving}
        okButtonProps={{
          disabled: !requested && !newAssignee,
          'data-testid': 'cc-clients-transfer-confirm',
        }}
        onOk={() => {
          if (requested) {
            close();
            return;
          }

          submit().catch(() => undefined);
        }}
        onCancel={close}
      >
        {requested ? (
          <Alert
            type="success"
            showIcon
            message={translate('portalClients.transfer.requested', {
              client: clientLabel,
              name: assigneeLabel,
            })}
            description={translate('portalClients.transfer.requestedNote')}
            data-testid="cc-clients-transfer-requested"
          />
        ) : (
          <Space direction="vertical" style={{ width: '100%' }} size="middle">
            <Text>
              {translate('portalClients.transfer.body', { client: clientLabel })}
            </Text>
            <Select
              style={{ width: '100%' }}
              placeholder={translate('portalClients.transfer.choosePractitioner')}
              value={newAssignee}
              onChange={setNewAssignee}
              options={options}
              data-testid="cc-clients-transfer-assignee"
            />
            <Text type="secondary">
              {translate('portalClients.transfer.consentNote')}
            </Text>
            {failure ? <Alert type="error" showIcon message={failure} /> : null}
          </Space>
        )}
      </Modal>
    </>
  );
};
